import { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";

// Utils
import { DataContext } from "../../../utils/DataContext";
import { UserAuth } from "../../../utils/AuthContext";
import useLanguage from "../../../utils/useLanguage";
import { formatDate } from "../../../utils/toDate";
import { onlyNums } from "../../../utils/OnlyNums";
import { sanitizeNumber } from "../../../utils/sanitizeNumber";

// Assets
import previousIcon from "../../../assets/previous_icon.png";
import nextIcon from "../../../assets/next_icon.png";
import addIcon from "../../../assets/add_icon.png";

// Styles
import classes from "../../../components/Modal.module.css";

// Components
import SectionDivider from "../../../components/SectionDivider";
import AddAnotherButton from "../../../components/AddAnotherButton";
import HotDropdown from "../../../components/HotDropdown";
import ErrorBox from "../../../components/ErrorBox";
import Loader from "../../../components/Loader";
import CustomInput from "../../../components/CustomInput";
import Dropdown from "../../../components/Dropdown";
import CustomButton from "../../../components/CustomButton";
import InformationBox from "../../../components/InformationBox";

const EditInvoiceOfMultiComponent = ({ invoice, setInvoice }) => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const containerDiv = useRef(null);

  const { error, selectedCustomer, customers, getCustomers } =
    useContext(DataContext);

  const { userData } = useContext(UserAuth);

  const [containerCustomer, setContainerCustomer] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);

  const [page, setPage] = useState(1);

  // Fill the current customers
  useEffect(() => {
    const allCustomers = invoice?.ShippedContainer?.map((shippedContainer) => {
      return shippedContainer?.customer;
    });
    setContainerCustomer((prev) => [...prev, ...allCustomers]);
  }, []);

  const handleAddInput = () => {
    const newContainer = {
      // containerNumber: `${container ${invoice?.shippedContainer + 1}}`,
      customerId: "",
      customerName: "",
      amountForSupplier: "",
      expectedArrivalDate: new Date(),
      finalDestination: "",
      material: "",
      unitPrice: "",
      numberOfBoxes: "",
      measuringUnit: "",
      shipper: "",
      shippingDate: new Date(),
      subCompany: "",
      exitDate: new Date(),
      abstractAmountForCustomer: "",
      currency: selectedCustomer?.currency || "",
      finalAmountForCustomer: "",
      dateOfEntry: new Date(),
      howWasPapersReceived: "receivedByCustomer",
      permitsReceivingDate: new Date(),
      notes: "",
    };

    setPage(invoice?.ShippedContainer?.length + 1);

    setInvoice((prev) => ({
      ...prev,
      ShippedContainer: [...prev?.ShippedContainer, newContainer], // Append new container
    }));

    setTimeout(() => {
      containerDiv.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleInputChange = (e, containerIndex = null, type) => {
    const { name, value } = e.target;

    setInvoice((prevState) => {
      if (containerIndex !== null) {
        const updatedContainers = prevState?.ShippedContainer.map(
          (container, index) => {
            return index === containerIndex
              ? {
                  ...container,
                  [name]: type === "date" ? new Date(value) : value,
                }
              : container;
          }
        );

        return { ...prevState, ShippedContainer: updatedContainers };
      }

      // Otherwise, update the top-level state field
      return { ...prevState, [name]: value };
    });
  };

  const handleNext = () => {
    if (page < invoice?.ShippedContainer?.length) setPage(page + 1);
    setTimeout(() => {
      containerDiv.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handlePrevious = () => {
    if (page > 1) setPage(page - 1);
    setTimeout(() => {
      containerDiv.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleInvoiceInputChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => ({ ...prev, [name]: value }));
  };

  const handleAmountChange = (e) => {
    const { name, value } = e.target;

    const parsedValue = value; // Extract single value

    setInvoice((prev) => {
      // Extract all relevant numbers from the previous state
      const { amountForSupplier, currencyRate } = prev;

      let updatedValues = { [name]: parsedValue };

      // Define dependencies and update accordingly
      if (name === "amountForSupplier") {
        updatedValues.abstractAmountForCustomer =
          parsedValue * (currencyRate || 1);
      } else if (name === "currencyRate") {
        updatedValues.abstractAmountForCustomer =
          (amountForSupplier || 0) * parsedValue;
      }
      // If the user type that value manually, empty the currencyRate value
      else if (name === "abstractAmountForCustomer") {
        updatedValues.currencyRate = 0;
      }

      return {
        ...prev,
        ...updatedValues,
      };
    });
  };

  const handleInvoiceDateChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => ({ ...prev, [name]: new Date(value) }));
  };

  const handleSelectCustomer = (customer) => {
    setContainerCustomer((prev) => ({
      ...prev,
      [page - 1]: customer,
    }));

    setInvoice((prev) => {
      const index = page - 1;
      if (!prev?.ShippedContainer) return prev;

      // Create a new array with the updated container
      const updatedShippedContainers = prev.ShippedContainer.map(
        (container, i) =>
          i === index
            ? {
                ...container,
                customerId: customer?.id,
                customer: { id: customer?.id, name: customer?.name },
              }
            : container
      );

      return {
        ...prev,
        ShippedContainer: updatedShippedContainers,
      };
    });
  };

  return (
    <div className="u-padding--bottom_400">
      {loadingModal ? <Loader /> : null}
      {error && !loadingModal && (
        <div className="fit-content u-margin-center">
          <ErrorBox>{t(error)}</ErrorBox>
        </div>
      )}
      <div className={` add-shipped-container-invoice-part`} ref={containerDiv}>
        <SectionDivider title="invoiceDetails" />
        <div className={classes.inputContainer}>
          <span>{t("subCompany")}: </span>
          <CustomInput
            name="subCompany"
            value={invoice?.subCompany?.nameInAr}
            onChange={() => null}
            flex
            readonly
            disabled
          />
        </div>
        <div className={classes.inputContainer}>
          <span>{t("invoiceNumber")}: </span>
          <div className={classes.invoiceNumberContainer}>
            <CustomInput
              name="invoiceNumber"
              value={invoice?.invoiceNumber}
              onChange={handleInvoiceInputChange}
              flex={true}
            />
          </div>
        </div>

        <div className={classes.inputContainer}>
          <span>{t("amountForSupplier")}: </span>
          <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
            <CustomInput
              name="amountForSupplier"
              value={invoice?.amountForSupplier}
              onChange={handleAmountChange}
              type="moneyV2"
              flex={true}
            />
            <div className="t-bold_bold">{invoice?.supplier?.currency}</div>
          </div>
        </div>

        <div className={classes.inputContainer}>
          <span>{t("currencyRate")}: </span>
          <div
            className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
          >
            <div className={classes.invoiceNumberContainer}>
              <CustomInput
                name="currencyRate"
                value={invoice?.currencyRate}
                onChange={handleAmountChange}
                type="moneyV2"
                flex={true}
              />
            </div>
            <div className="t-bold_bold">{invoice?.customer?.currency}</div>
          </div>
        </div>
        <div className={classes.inputContainer}>
          <span>{t("abstractAmountForCustomer")}: </span>
          <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
            <CustomInput
              name="abstractAmountForCustomer"
              value={invoice?.abstractAmountForCustomer}
              onChange={handleAmountChange}
              type="moneyV2"
              flex={true}
            />
            <div className="t-bold_bold">{invoice?.customer?.currency}</div>
          </div>
        </div>
        <div className={classes.inputContainer}>
          <span>{t("Date")}: </span>
          <CustomInput
            name="date"
            type="date"
            flex={true}
            value={formatDate(invoice?.date)}
            onChange={(e) => handleInvoiceDateChange(e)}
          />
        </div>

        <div className={classes.inputContainer}>
          <span>{t("received")}: </span>
          <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
            <CustomInput
              name="received"
              value={invoice?.received}
              onChange={handleAmountChange}
              type="moneyV2"
              flex={true}
              readonly
            />
            <div className="t-bold_bold">{invoice?.customer?.currency}</div>
          </div>
        </div>
        <div className={classes.inputContainer}>
          <span>{t("left")}: </span>
          <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
            <CustomInput
              name="left"
              value={invoice?.left}
              onChange={handleAmountChange}
              onKeyDown={(e) => onlyNums(e)}
              type="moneyV2"
              flex={true}
              readonly
            />
            <div className="t-bold_bold">{invoice?.customer?.currency}</div>
          </div>
        </div>

        <div className={classes.inputContainer}>
          <span className={classes.notesClass}>{t("notes")}: </span>
          <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
            <CustomInput
              name="notes"
              value={invoice?.notes}
              onChange={handleInputChange}
              type="textarea"
              flex={true}
            />
          </div>
        </div>
      </div>
      {/* Containers */}
      <SectionDivider title="containersDetails" />
      {invoice?.ShippedContainer?.length ? (
        <div
          className={`add-shipped-container-container-part ${classes.flexableForm} ${classes.flexableFormWithPadding}`}
        >
          {invoice?.ShippedContainer?.map((item, index) => {
            if (index + 1 === page) {
              return (
                <>
                  <div className={classes.inputContainer}>
                    <span>{t("customerName")}: </span>
                    <div className={classes.invoiceNumberContainer}>
                      <HotDropdown
                        initOptions={customers}
                        selected={
                          containerCustomer[index]?.id
                            ? containerCustomer[index]
                            : { id: "", name: `--${t("chooseACustomer")}--` }
                        }
                        getData={getCustomers}
                        setSelected={handleSelectCustomer}
                        flex={true}
                        errorText={error}
                        optionsName={"customers"}
                      />
                    </div>
                  </div>
                  <div className={classes.inputContainer}>
                    <span>{t("containerNumber")}: </span>
                    <div className={classes.invoiceNumberContainer}>
                      <CustomInput
                        name="containerNumber"
                        value={item?.containerNumber}
                        onChange={(e) => handleInputChange(e, page - 1)}
                        flex
                      />
                    </div>
                  </div>

                  {/* <div className={classes.inputContainer}>
                    <span>{t("amountForSupplier")}: </span>
                    <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
                      <CustomInput
                        name="amountForSupplier"
                        value={item?.amountForSupplier}
                        onChange={(e) => handleInputChange(e, page - 1)}
                        type="moneyV2"
                        flex={true}
                      />
                      <div className="t-bold_bold">
                        {invoice?.supplier?.currency}
                      </div>
                    </div>
                  </div> */}

                  <div className={classes.inputContainer}>
                    <span>{t("expectedArrivalDate")}: </span>
                    <CustomInput
                      name="expectedArrivalDate"
                      type="date"
                      value={formatDate(item?.expectedArrivalDate)}
                      onChange={(e) => handleInputChange(e, page - 1, "date")}
                      flex
                    />
                  </div>

                  <div className={classes.inputContainer}>
                    <span>{t("finalDestination")}: </span>
                    <CustomInput
                      name="finalDestination"
                      value={item?.finalDestination}
                      onChange={(e) => handleInputChange(e, page - 1)}
                      flex={true}
                    />
                  </div>

                  <div className={classes.inputContainer}>
                    <span>{t("material")}: </span>
                    <CustomInput
                      name="material"
                      value={item?.material}
                      onChange={(e) => handleInputChange(e, page - 1)}
                      flex={true}
                    />
                  </div>

                  {/* <div className={classes.inputContainer}>
                    <span>{t("unitPrice")}: </span>
                    <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
                      <CustomInput
                        name="unitPrice"
                        value={item?.unitPrice}
                        onChange={(e) => handleInputChange(e, page - 1)}
                        flex={true}
                      />
                      <div className="t-bold_bold">
                        {invoice?.supplier?.currency}
                      </div>
                    </div>
                  </div> */}

                  <div className={classes.inputContainer}>
                    <span>{t("numberOfBoxes")}: </span>
                    <CustomInput
                      name="numberOfBoxes"
                      value={item?.numberOfBoxes}
                      onChange={(e) => handleInputChange(e, page - 1)}
                      type="moneyV2"
                      flex={true}
                    />
                  </div>

                  <div className={classes.inputContainer}>
                    <span>{t("measuringUnit")}: </span>
                    <CustomInput
                      name="measuringUnit"
                      value={item?.measuringUnit}
                      onChange={(e) => handleInputChange(e, page - 1)}
                      flex={true}
                    />
                  </div>

                  <div className={classes.inputContainer}>
                    <span>{t("shipper")}: </span>
                    <CustomInput
                      name="shipper"
                      value={item?.shipper}
                      onChange={(e) => handleInputChange(e, page - 1)}
                      flex={true}
                    />
                  </div>

                  {/* Date of Entry */}
                  <div className={classes.inputContainer}>
                    <span>{t("dateOfEntry")}: </span>
                    <CustomInput
                      name="dateOfEntry"
                      type="date"
                      flex={true}
                      value={formatDate(item?.dateOfEntry)}
                      onChange={(e) => handleInputChange(e, page - 1, "date")}
                    />
                  </div>

                  {/* How Was Papers Received */}
                  <div className={classes.inputContainer}>
                    <span>{t("howWasPapersReceived")}: </span>
                    <Dropdown
                      options={[
                        { title: "receivedByUs", value: "receivedByUs" },
                        {
                          title: "receivedByCustomer",
                          value: "receivedByCustomer",
                        },
                      ]}
                      selected={
                        item?.howWasPapersReceived === "receivedByUs"
                          ? {
                              title: "receivedByUs",
                              value: "receivedByUs",
                            }
                          : {
                              title: "receivedByCustomer",
                              value: "receivedByCustomer",
                            }
                      }
                      setSelected={(status) => {
                        handleInputChange(
                          {
                            target: {
                              name: "howWasPapersReceived",
                              value: status?.value,
                            },
                          },
                          page - 1
                        );
                      }}
                      flex
                    />
                  </div>

                  {/* Shipping Date */}
                  <div className={classes.inputContainer}>
                    <span>{t("shippingDate")}: </span>
                    <CustomInput
                      name="shippingDate"
                      type="date"
                      flex={true}
                      value={formatDate(item?.shippingDate)}
                      onChange={(e) => handleInputChange(e, page - 1, "date")}
                    />
                  </div>

                  {/* Exit Date */}
                  <div className={classes.inputContainer}>
                    <span>{t("exitDate")}: </span>
                    <CustomInput
                      name="exitDate"
                      type="date"
                      flex={true}
                      value={formatDate(item?.exitDate)}
                      onChange={(e) => handleInputChange(e, page - 1, "date")}
                    />
                  </div>

                  {/* Abstract Amount For Customer */}
                  {/* <div className={classes.inputContainer}>
                    <span>{t("abstractAmountForCustomer")}: </span>
                    <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
                      <CustomInput
                        name="abstractAmountForCustomer"
                        value={item?.abstractAmountForCustomer}
                        onChange={(e) => handleInputChange(e, page - 1)}
                        type="moneyV2"
                        flex={true}
                      />
                      <div className="t-bold_bold">
                        {invoice?.customer?.currency}
                      </div>
                    </div>
                  </div> */}

                  {/* Final Amount For Customer */}
                  <div className={classes.inputContainer}>
                    <span>{t("finalAmountForCustomer")}: </span>
                    <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
                      <CustomInput
                        name="finalAmountForCustomer"
                        value={item?.finalAmountForCustomer}
                        onChange={(e) => handleInputChange(e, page - 1)}
                        type="moneyV2"
                        flex={true}
                      />
                      <div className="t-bold_bold">
                        {invoice?.customer?.currency}
                      </div>
                    </div>
                  </div>

                  {/* Permits Receiving Date */}
                  <div className={classes.inputContainer}>
                    <span>{t("permitsReceivingDate")}: </span>
                    <CustomInput
                      name="permitsReceivingDate"
                      type="date"
                      flex={true}
                      value={formatDate(item?.permitsReceivingDate)}
                      onChange={(e) => handleInputChange(e, page - 1, "date")}
                    />
                  </div>

                  {/* Delivered or NOT delivered */}
                  <div className={classes.inputContainer}>
                    <span>{t("deliveryStatus")}: </span>
                    <Dropdown
                      options={[
                        { title: "delivered", value: true },
                        {
                          title: "notDelivered",
                          value: false,
                        },
                      ]}
                      selected={
                        item?.delivered
                          ? {
                              title: "delivered",
                              value: true,
                            }
                          : {
                              title: "notDelivered",
                              value: false,
                            }
                      }
                      setSelected={(status) => {
                        handleInputChange(
                          {
                            target: {
                              name: "delivered",
                              value: status?.value,
                            },
                          },
                          page - 1
                        );
                      }}
                      flex
                    />
                  </div>

                  {/* Notes */}
                  <div className={classes.inputContainer}>
                    <span className={classes.notesClass}>{t("notes")}: </span>
                    <CustomInput
                      name="notes"
                      type="textarea"
                      value={item?.notes || ""}
                      onChange={(e) => handleInputChange(e, page - 1)}
                      flex={true}
                    />
                  </div>
                </>
              );
            }
            return null;
          })}
        </div>
      ) : (
        <InformationBox>{t("noContainers")}</InformationBox>
      )}
      <div>
        {invoice?.ShippedContainer?.length ? (
          <div className={"add-shipped-container-next-previous"}>
            <CustomButton
              variant="blank"
              title={t("previous")}
              onClick={handlePrevious}
              disabled={page === 1}
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={15}
            />
            <CustomButton
              title={t("next")}
              variant="blank"
              onClick={handleNext}
              disabled={page === invoice?.ShippedContainer?.length}
              icon={currentDirection === "ltr" ? nextIcon : previousIcon}
              iconSize={15}
              reverseIcon
            />
          </div>
        ) : null}
      </div>
      <div className={"add-shipped-container-add-another"}>
        <AddAnotherButton
          onClick={handleAddInput}
          variant="blank"
          title={t("addContainer")}
          icon={addIcon}
          iconSize={16}
          disabled={loadingModal}
          arrayLength={invoice?.ShippedContainer?.length}
        />
      </div>
    </div>
  );
};

export default EditInvoiceOfMultiComponent;
